<script lang="ts">
	import { secondarySourcesMenu } from '#lib/menu/secondary-sources-menu.ts';
	import ModelSelect from '#lib/ModelSelect.svelte';
	import { globalState } from '#lib/util/global-state.svelte.ts';
	import { SECONDARY_SOURCE_ICONS } from '#lib/util/secondary-source-icons.ts';
	import { getAvailableSecondarySources } from '#lib/util/secondary-sources.ts';
	import { trackEvent } from '#lib/util/tracking.ts';
	import { tooltip } from '@astlai/common/tooltip/tooltip.svelte.ts';
	import Gate from '#lib/Gate.svelte';
	import AttachmentChips from '#lib/AttachmentChips.svelte';
	import { getAttachmentsContext } from '#src/contexts/attachments.context.svelte.ts';
	import { ATTACHMENT_ACCEPT } from '#lib/util/chat-attachments.svelte.ts';
	import IconAddFile from 'virtual:icons/material-symbols/add-2-rounded';
	import IconUp from 'virtual:icons/material-symbols/arrow-upward-rounded';
	import IconSourcesOff from 'virtual:icons/material-symbols/globe';
	import IconStop from 'virtual:icons/material-symbols/stop-rounded';
	import { t } from './i18n/tranlsator.svelte';
	import type { I18nKey } from './i18n/translations';

	interface Props {
		value: string;
		showSettings?: boolean;
		model?: string;
		onchange?: () => void;
		onabort?: () => void;
		disabled?: boolean;
		isStreaming?: boolean;
		appearance?: 'footer' | 'hero';
		placeholder?: string;
	}

	let {
		disabled,
		isStreaming = false,
		value = $bindable(),
		model = $bindable(),
		showSettings = false,
		onchange = () => {},
		onabort = () => {},
		appearance = 'footer',
		placeholder = t('chat.placeholder')
	}: Props = $props();

	let inputEl: HTMLTextAreaElement = $state(null);
	let fileInput = $state<HTMLInputElement>();

	// Set by the page, which also sends the files with the message.
	const attachmentsCtx = getAttachmentsContext();
	const allowAttachments = $derived(attachmentsCtx?.enabled ?? false);

	const twoRowLayout = $derived(appearance === 'hero' && globalState.user?.role !== 'user');

	const creditsExhausted = $derived(globalState.usageStatus === 'exhausted');

	const sendButtonDisabled = $derived.by(() => {
		void value; // reactive dependency
		return !inputEl?.validity.valid || disabled || creditsExhausted || attachmentsCtx?.blocksSending;
	});

	function handleFileChange(e: Event) {
		const input = e.currentTarget as HTMLInputElement;
		if (input.files?.length) {
			attachmentsCtx?.add(input.files);
		}
		// Picking the same file again after removing it should work too.
		input.value = '';
	}

	const availableSources = $derived(getAvailableSecondarySources(globalState.instance));
	const enabledSources = $derived(
		availableSources.filter((source) => globalState.userSettings?.[source.settingsKey])
	);
	const sourcesTooltip = $derived.by(() => {
		const enabledNames =
			enabledSources.length > 0
				? enabledSources.map((source) => t(`chat.sources.${source.flagKey}` as I18nKey))
				: [t('chat.secondarySources.none')];
		return `${t('chat.secondarySources')}: ${enabledNames.join(', ')}`;
	});

	async function handleInput(e: Event) {
		e.preventDefault();
		if (value) {
			trackEvent('chat_message_sent');
			onchange();
		}
	}

	function handleButtonClick(e: Event) {
		e.preventDefault();
		if (isStreaming) {
			trackEvent('chat_aborted');
			onabort();
		} else {
			handleInput(e);
		}
	}

	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			// Same rule as the send button, which turns into stop while streaming.
			if (!isStreaming && !sendButtonDisabled) {
				handleInput(e);
			}
		}
	}
</script>

<form
	class:hero={appearance === 'hero'}
	onsubmit={handleInput}
	class="input-form"
	class:two-row-layout={twoRowLayout}
>
	{#if attachmentsCtx?.items.length}
		<div class="attachments">
			<AttachmentChips
				items={attachmentsCtx.items}
				limitMessage={attachmentsCtx.limitMessage}
				onremove={(item) => attachmentsCtx.remove(item)}
			/>
		</div>
	{/if}

	<div class="input-row">
		<div class="textarea-wrapper" data-replicated-value={value}>
			<textarea
				bind:this={inputEl}
				required
				name="userInput"
				rows="1"
				bind:value
				{placeholder}
				disabled={creditsExhausted}
				onkeydown={handleKeydown}></textarea>
		</div>

		<div class="options">
			{#if allowAttachments || availableSources.length > 0}
				<div class="tools">
					{#if allowAttachments}
						<input
							bind:this={fileInput}
							class="file-input"
							type="file"
							accept={ATTACHMENT_ACCEPT}
							multiple
							onchange={handleFileChange}
						/>
						<button
							type="button"
							class="icon-button attach-button"
							use:tooltip={t('chat.attach')}
							aria-label={t('chat.attach')}
							onclick={() => fileInput?.click()}
						>
							<IconAddFile />
						</button>
					{/if}
					{#if availableSources.length > 0}
						<button
							type="button"
							class="icon-button sources-button"
							class:active={enabledSources.length > 0}
							use:tooltip={sourcesTooltip}
							aria-label={t('chat.secondarySources')}
							{@attach secondarySourcesMenu({ sources: () => availableSources })}
						>
							{#if enabledSources.length > 0}
								{#each enabledSources as source (source.flagKey)}
									{@const SourceIcon = SECONDARY_SOURCE_ICONS[source.flagKey]}
									<SourceIcon />
								{/each}
							{:else}
								<IconSourcesOff />
							{/if}
						</button>
					{/if}
				</div>
			{/if}

			<div class="controls">
				{#if showSettings}
					<Gate minimumPlan="essential">
						<ModelSelect bind:model />
					</Gate>
				{/if}
				<button
					type="button"
					class="accent"
					class:stop={isStreaming}
					disabled={!isStreaming && sendButtonDisabled}
					onclick={handleButtonClick}
					aria-label={isStreaming ? t('chat.stopResponse') : ''}
				>
					{#if isStreaming}
						<IconStop />
					{:else}
						<IconUp />
					{/if}
				</button>
			</div>
		</div>
	</div>
</form>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.input-form {
		// The attachment chips, if any, above the input row.
		@include lib.flex-column();
		border-radius: 0.75rem;
		@include lib.glass-surface();

		.file-input {
			display: none;
		}

		.attachments {
			padding: 1rem 1rem 0;
		}

		.input-row {
			@include lib.flex-row();
		}

		&.two-row-layout {
			.input-row {
				@include lib.flex-column();
			}
			.options {
				padding: 0 1rem 1rem 0.5rem;
			}
			&.hero .input-row > .textarea-wrapper textarea {
				padding-bottom: 1rem;
			}
		}

		&.hero {
			.input-row > .textarea-wrapper {
				textarea {
					padding: 1rem;
				}
			}
		}

		.textarea-wrapper {
			display: grid;
			flex: 1;
			max-height: 12rem;
			overflow: auto;

			&::after {
				content: attr(data-replicated-value) ' ';
				white-space: pre-wrap;
				visibility: hidden;
				word-wrap: break-word;
				padding: 1rem;
				font: inherit;
				font-size: 1rem;
				line-height: 1.5rem;
				box-sizing: border-box;
				grid-area: 1 / 1 / 2 / 2;
			}

			textarea {
				grid-area: 1 / 1 / 2 / 2;
				background: transparent;
				border: none;
				font: inherit;
				font-size: 1rem;
				line-height: 1.5rem;
				padding: 1rem;
				resize: none;
				overflow-y: auto;
				box-sizing: border-box;
				field-sizing: content;
				&:focus-visible {
					outline: none;
				}
			}
		}

		.options {
			@include lib.flex-row();
			@include lib.flex-align(space-between, center);
			gap: 0.5rem;
			padding-right: 1rem;
			padding-top: 0;

			.tools {
				@include lib.flex-row();
				gap: 0.25rem;

				.attach-button {
					color: var(--text-secondary);
				}

				.sources-button {
					position: relative;
					color: var(--text-secondary);

					&.active {
						color: var(--text-accent);
						// background: var(--surface-accent-subtle);
						width: auto;
						gap: 0.5rem;
						padding: 0 0.5rem;
					}
				}
			}

			.controls {
				@include lib.flex-row();
				@include lib.flex-align(flex-end, center);
				gap: 1rem;
				flex: 1 1 auto;
				justify-self: flex-end;
			}

			button {
				flex: 0 0 auto;
				min-width: 0;
				width: 32px;
				height: 32px;
			}
		}
	}
</style>
